/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextInput } from '@carbon/react';
import { View, ViewOff } from '@carbon/react/icons';
import Invoker from '../utils/invoker';
import { getMixedContentMessage } from '../utils/error-message-handler';

function TestToolAccessCode({ config, onChange }) {
  const [authToken, setAuthToken] = useState({ code: '' });
  const [authError, setAuthError] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const invoker = useMemo(() => new Invoker(), []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!authToken.code) {
      setAuthToken({ code: '' });
    }
  }, [authToken.code]);

  const authorize = () => {
    const authToken = {
      code: ''
    };

    // mixed content check - i.e. calling http from https - not allowed.
    if (window.location.protocol === 'https:' && config.flags.selectedTokenUrl.lastIndexOf(window.location.protocol) !== 0) {
      setAuthToken(authToken);
      setAuthError(getMixedContentMessage(t));
      return;
    }

    // authorize calls call window.open, we can't get a handle on the response so just call it.
    invoker.authorize(config.flags, config.identification.clientID, config.scopes, config.identification.redirectUri);
  };

  const updateCode = (event) => {
    const newToken = { code: event.target.value };
    setAuthToken(newToken);

    if (onChange && typeof onChange === 'function') {
      onChange(newToken);
    }
  };

  return (
    <div className="accessCode">
      <Button className="accessCodeButton" onClick={authorize}>
        {t('explorer_authorize')}
      </Button>
      <TextInput
        className="accessCodeField"
        labelText={t('access_code')}
        type={showPassword ? 'text' : 'password'}
        id="accessCodeField"
        value={authToken.code}
        onChange={updateCode}
      />
      <Button
        hasIconOnly
        className="togglePassword"
        kind="ghost"
        iconDescription={showPassword ? 'Hide access code' : 'Show access code'}
        renderIcon={showPassword ? ViewOff : View}
        onClick={() => {
          setShowPassword(!showPassword);
        }}
      />
      {authError && authError.highlightedText && (
        <div className="authError">
          <pre>
            <code className="boundedSection hljs oauthError" dangerouslySetInnerHTML={authError.highlightedText} />
          </pre>
        </div>
      )}
    </div>
  );
}

export default TestToolAccessCode;
